import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { CalendarX, Download, Loader2, RefreshCw, Users, HelpCircle, TrendingDown } from 'lucide-react';
import { fmtDate } from './demoConstants';
import DemoPeriodSelector from './DemoPeriodSelector';
import { PageHeader, Stat } from './DemoUI';
import DemoDetailModal from './DemoDetailModal';
import DemoDrilldownModal from './DemoDrilldownModal';

const csv = (rows) => {
  const head = ['Demo', 'Demo Date', 'Student', 'Course', 'Trainer', 'Counsellor', 'State', 'Reason', 'Note', 'Marked By', 'Role', 'Marked At'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows.map((r) => [r.demo_code, r.demo_date, r.student_name, r.course_name, r.trainer_name,
    r.counsellor_name, r.status === 'not_applicable' ? 'Not Applicable' : 'Not Conducted', r.reason, r.note,
    r.marked_by, r.marked_by_role, r.marked_at].map(esc).join(','));
  return [head.map(esc).join(','), ...body].join('\n');
};

const BAR = ['bg-rose-500', 'bg-amber-500', 'bg-violet-500', 'bg-sky-500', 'bg-emerald-500', 'bg-indigo-500', 'bg-slate-400'];

export default function DemoNotConductedPage({ embedded = false }) {
  const [period, setPeriod] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState('');
  const [openId, setOpenId] = useState(null);
  const [drill, setDrill] = useState(null);

  const params = useMemo(() => ({ date_from: period?.from_date, date_to: period?.to_date }), [period]);

  const load = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    try {
      const r = await api.get('/training/demos/reports/not-conducted', { params });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load the report'); }
    finally { setLoading(false); }
  }, [period, params]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};
  const all = data?.rows || [];
  const rows = reason ? all.filter((r) => r.reason === reason) : all;
  const keys = data?.reason_keys || [];
  const openDrill = (title, subtitle, list) => setDrill({ title, subtitle, rows: list });

  const downloadCsv = () => {
    const blob = new Blob([csv(rows)], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `demos-not-conducted-${period?.from_date}_${period?.to_date}.csv`;
    a.click();
  };

  return (
    <div className={embedded ? 'space-y-6' : 'space-y-8'} data-testid="not-conducted-page">
      <PageHeader embedded={embedded} icon={CalendarX} eyebrow="Trainer Demo" title="Demos Not Conducted"
        subtitle="What keeps killing demo slots — counted by month and by the reason the team recorded."
        testid="not-conducted-header">
        <Button variant="outline" size="sm" className="h-10" onClick={downloadCsv} disabled={!rows.length} data-testid="nc-export-csv">
          <Download className="w-4 h-4 mr-1.5" />CSV
        </Button>
        <Button variant="outline" size="sm" className="h-10" onClick={load} disabled={loading} data-testid="nc-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </PageHeader>

      <DemoPeriodSelector onChange={setPeriod} testid="nc-period" />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="nc-kpi-rail">
        <Stat icon={CalendarX} label="Not Conducted" value={t.not_conducted ?? 0} tone="rose" sub={`of ${t.demos ?? 0} demos`}
          onClick={() => openDrill('Not conducted', 'Demos that never happened', all.filter((r) => r.status === 'not_done'))} testid="nckpi-not-done" />
        <Stat icon={TrendingDown} label="Slot Loss" value={`${t.pct_of_demos ?? 0}%`} tone="amber" sub="share of all demos in the period" testid="nckpi-pct" />
        <Stat icon={HelpCircle} label="Not Applicable" value={t.not_applicable ?? 0} tone="slate" sub="duplicates / created by mistake"
          onClick={() => openDrill('Not applicable', 'Marked not applicable — duplicates and mistakes', all.filter((r) => r.status === 'not_applicable'))} testid="nckpi-na" />
        <Stat icon={CalendarX} label="Top Reason" value={t.top_reason_count ?? 0} tone="violet" sub={t.top_reason || '—'}
          onClick={() => t.top_reason && openDrill(t.top_reason, 'Most common reason in this period', all.filter((r) => r.reason === t.top_reason))} testid="nckpi-top-reason" />
        <Stat icon={HelpCircle} label="No Reason Given" value={t.no_reason ?? 0} tone="slate" sub="older records only"
          onClick={() => openDrill('Reason not recorded', 'Marked before reasons were mandatory — check the audit trail', all.filter((r) => r.reason === 'Reason not recorded'))} testid="nckpi-no-reason" />
      </div>

      {/* Reason share */}
      <Card className="p-5 border space-y-3" data-testid="nc-reason-share">
        <div className="flex items-center justify-between">
          <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">Why demos died</h2>
          {reason && <button onClick={() => setReason('')} className="text-[11.5px] text-violet-600 hover:underline" data-testid="nc-clear-reason">Clear filter</button>}
        </div>
        {(data?.by_reason || []).length === 0 && <p className="text-[12px] text-slate-400">Nothing was marked not conducted in this period.</p>}
        {(data?.by_reason || []).map((r, i) => (
          <button key={r.reason} onClick={() => setReason(reason === r.reason ? '' : r.reason)}
            className={`w-full text-left group ${reason === r.reason ? '' : 'opacity-95'}`} data-testid={`nc-reason-${r.reason}`}>
            <div className="flex items-center justify-between text-[12px] mb-1">
              <span className={`font-semibold ${reason === r.reason ? 'text-violet-700' : 'text-slate-700'} group-hover:text-violet-700`}>{r.reason}</span>
              <span className="tabular-nums text-slate-500">{r.count} · {r.pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full transition-all ${BAR[i % BAR.length]}`} style={{ width: `${Math.max(r.pct, 2)}%` }} />
            </div>
          </button>
        ))}
      </Card>

      {/* Month x reason matrix */}
      <Card className="border overflow-hidden" data-testid="nc-month-matrix">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/70">
          <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">Month by month</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
              <th className="px-4 py-3">Month</th><th className="px-4 py-3 text-right">Not Conducted</th><th className="px-4 py-3 text-right">Of Demos</th>
              {keys.map((k) => <th key={k} className="px-4 py-3 text-right whitespace-nowrap">{k}</th>)}
            </tr></thead>
            <tbody>
              {(data?.by_month || []).map((m) => (
                <tr key={m.month} className="border-b border-slate-100 last:border-0 hover:bg-violet-50/60 cursor-pointer transition-colors"
                  onClick={() => openDrill(m.label, `${m.total} demo(s) not conducted`, all.filter((r) => (r.demo_date || '').slice(0, 7) === m.month))}
                  data-testid={`nc-month-${m.month}`}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{m.label}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-rose-700">{m.total}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-500">{m.pct_of_demos}% <span className="text-slate-400">of {m.demos}</span></td>
                  {keys.map((k) => <td key={k} className="px-4 py-3 text-right tabular-nums text-slate-600">{m.reasons?.[k] || '—'}</td>)}
                </tr>
              ))}
              {(data?.by_month || []).length === 0 && <tr><td colSpan={3 + keys.length} className="px-4 py-6 text-center text-[12px] text-slate-400">No data for this period.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Who is marking them */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[['Counsellors', data?.by_counsellor], ['Trainers', data?.by_trainer]].map(([label, list]) => (
          <Card key={label} className="p-4 border space-y-2" data-testid={`nc-by-${label.toLowerCase()}`}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{label} with lost slots</p>
            {(list || []).slice(0, 8).map((x) => (
              <div key={x.name} className="flex items-center justify-between text-[12.5px]">
                <span className="text-slate-700">{x.name}</span>
                <span className="tabular-nums font-semibold text-slate-900">{x.count}</span>
              </div>
            ))}
            {(list || []).length === 0 && <p className="text-[12px] text-slate-400">Nothing here.</p>}
          </Card>
        ))}
      </div>

      {/* Rows */}
      <Card className="border overflow-hidden" data-testid="nc-rows-card">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/70 flex items-center justify-between">
          <h2 className="text-[13px] font-semibold text-slate-700 uppercase tracking-wider">
            {reason ? `${reason} · ${rows.length} demo(s)` : `Every lost slot · ${rows.length}`}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
              <th className="px-4 py-3">Demo</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Student</th>
              <th className="px-4 py-3">Trainer</th><th className="px-4 py-3">Counsellor</th>
              <th className="px-4 py-3">Reason</th><th className="px-4 py-3">Marked By</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setOpenId(r.id)} className="border-b border-slate-100 last:border-0 hover:bg-violet-50 cursor-pointer transition-colors" data-testid={`nc-row-${r.demo_code}`}>
                  <td className="px-4 py-3 font-mono text-[11px] text-slate-700">
                    {r.demo_code}
                    {r.status === 'not_applicable' && <span className="block text-[9.5px] uppercase tracking-wider text-slate-400">not applicable</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(r.demo_date)}</td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-slate-800">{r.student_name}</span>
                    <span className="block text-[10.5px] text-slate-400">{r.course_name}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.trainer_name}</td>
                  <td className="px-4 py-3 text-slate-600">{r.counsellor_name}</td>
                  <td className="px-4 py-3">
                    <span className="text-slate-800">{r.reason}</span>
                    {r.note && <span className="block text-[10.5px] text-slate-500">{r.note}</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {r.marked_by}
                    {r.marked_by_role && <span className="block text-[10px] uppercase tracking-wider text-slate-400">{r.marked_by_role}</span>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[12px] text-slate-400" data-testid="nc-empty">No demos were marked not conducted in this period.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <DemoDetailModal demoId={openId} onClose={() => setOpenId(null)} onChanged={load} />
      <DemoDrilldownModal open={!!drill} onClose={() => setDrill(null)} title={drill?.title} subtitle={drill?.subtitle}
        rows={drill?.rows || []} onOpenDemo={(id) => { setDrill(null); setOpenId(id); }} />
    </div>
  );
}
